import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useConfirmationModal } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useIsDataExportAllowed } from '~/_shared/types/toolsAndFeatures/useIsDataExportAllowed';
import { UndoItemType } from '~/_shared/types/undo/undoItem';
import { useTranslation } from '~/_shared/utils/hooks';
import { useStorageService } from '~/_shared/utils/storageService';
import { useModal } from '~/modal/useModal.hook';
import { useToolCollapseState } from '~/sidebar/sidebarApps/mapTools/useToolCollapseState';
import { clearDirectionsRoutes } from '~/store/mapSettings/directions/mapSettingsDirections.actionCreators';
import { useMapSettingsDirectionsRoutesSelector } from '~/store/mapSettings/directions/mapSettingsDirections.selectors';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { createHistoryItem } from '~/store/undo/undo.helpers';
import { DirectionsPanelComponent } from './directionsPanel.component';

export const DirectionsPanelContainer: FC = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const mapId = useMapIdSelector();
  const storageService = useStorageService();
  const routes = useMapSettingsDirectionsRoutesSelector();
  const { openModal } = useModal('ExportDirections');
  const { isToolCollapsed } = useToolCollapseState();
  const allowExportLocations = useIsDataExportAllowed(MapTool.Routing);
  const { openConfirmationModal, closeConfirmationModal } = useConfirmationModal();

  const onExportFile = useCallback(() => {
    openModal({
      showSelectRoutesSection: true,
      selectedRouteIds: [],
    });
  }, [openModal]);

  const deleteAllRoutes = useCallback(() => {
    openConfirmationModal({
      title: t('Delete all'),
      text: t('directionsTool.deleteAll.warning'),
      onConfirm: () => {
        if (mapId) {
          routes.forEach(route => {
            createHistoryItem(storageService, {
              type: UndoItemType.REMOVE_ROUTE,
              data: route,
              mapId,
            });
          });
          dispatch(clearDirectionsRoutes());
          closeConfirmationModal();
        }
      },
      onClose: closeConfirmationModal,
      onCancel: closeConfirmationModal,
      isConfirmButtonDestructive: true,
    });
  }, [
    openConfirmationModal, t, closeConfirmationModal, mapId, routes,
    dispatch, storageService,
  ]);

  return (
    <DirectionsPanelComponent
      exportFile={allowExportLocations ? onExportFile : undefined}
      isLegendMode={isToolCollapsed}
      routes={routes}
      deleteAllRoutes={deleteAllRoutes}
    />
  );
};
